import { isDefined } from "@omni/utils/helper.utils";
import { createContext, ReactNode, use } from "react";

import { AemEnergyLabelRenditions } from "@/components/Product/energyLabel/utils";
import { Config } from "@/utils/config.utils";

type EnergyLabelContextValue = {
    labelNested: string;
    altText: string;
} & (
    | {
          eprel: true;
          label: string;
          fiche: string;
      }
    | {
          eprel: false;
          label: AemEnergyLabelRenditions | null;
      }
);

type EnergyLabelContextProviderProps = {
    eprelId: string | null;
    label: AemEnergyLabelRenditions | null;
    labelNested: string | null;
    altText: string;
    children: ReactNode;
};

export const EnergyLabelContext = createContext<
    EnergyLabelContextValue | undefined
>(undefined);

export const EnergyLabelContextProvider = ({
    eprelId,
    label,
    labelNested,
    altText,
    children
}: EnergyLabelContextProviderProps) => {
    if (eprelId) {
        return (
            <EnergyLabelContext
                value={{
                    eprel: true,
                    labelNested: `https://eprel.ec.europa.eu/api/product/${eprelId}/nested-label`,
                    label: `https://eprel.ec.europa.eu/api/product/${eprelId}/labels?format=SVG`,
                    fiche: `https://eprel.ec.europa.eu/api/product/${eprelId}/fiches?language=${Config.getLanguage().toUpperCase()}`,
                    altText: altText
                }}
            >
                {children}
            </EnergyLabelContext>
        );
    } else if (labelNested) {
        return (
            <EnergyLabelContext
                value={{
                    eprel: false,
                    labelNested: labelNested,
                    label: label,
                    altText: altText
                }}
            >
                {children}
            </EnergyLabelContext>
        );
    } else {
        return null;
    }
};

export const useEnergyLabelContext = () => {
    const context = use<EnergyLabelContextValue | undefined>(
        EnergyLabelContext
    );

    if (!isDefined(context)) {
        throw new Error(`${EnergyLabelContext.displayName} is undefined.`);
    }

    return context;
};
